import "./App.css";

import React from "react";
import { useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { useMaterialUIController } from "./context";
import MDBox from "./components/MDBox";
import Sidenav from "./examples/Sidenav";

import { createAppTheme } from "./assets/theme";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import routes from "./routes";

import { Login } from "app/components/login/Login";
import { Logout } from "app/components/login/Logout";
import { ForgotPassword } from "app/components/forgotPassword/ForgotPassword";
import {
  getLandingRoute,
  getLoggedInUserRoles,
  isAuthenticated,
  readSessionToken,
} from "app/services/app.state.service";
import { isEmpty } from "utils/validationUtil";
import { Header } from "app/components/header/Header";
import { ToastHost } from "app/components/common/ToastHost";
// import { TeamActivitiesRouterFactory } from "app/components/factories/TeamActivitiesRouterFactory";
import { DashboardRouterFactory } from "app/components/factories/DashboardRouterFactory";
import { PublicOnlyRoute } from "app/components/factories/PublicOnlyRoute";
import { TeamMembersRouterFactory } from "app/components/factories/TeamMembersRouterFactory";
import { LeavePlanRouterFactory } from "app/components/factories/LeavePlanRouterFactory";
import { ChangePasswordRouterFactory } from "app/components/factories/ChangePasswordRouterFactory";
import { AdminResetPasswordRouterFactory } from "app/components/factories/AdminResetPasswordRouterFactory";
import { UsageAnalyticsRouterFactory } from "app/components/factories/UsageAnalyticsRouterFactory";
import { Provider } from "react-redux";
import store from "./store/index";
import { Footer } from "app/components/footer/Footer";
import { Grid } from "@mui/material";
import { MasterRouterFactory } from "app/components/factories/MasterRouterFactory";
import { ActivityTypeRouterFactory } from "app/components/factories/ActivityTypeRouterFactory";
import { WorkingHoursRouterFactory } from "app/components/factories/WorkingHoursRouterFactory";
import { ExportReportsRouterFactory } from "app/components/factories/ExportReportsRouterFactory";
import { SkillMatrixRouterFactory } from "app/components/factories/SkillMatrixRouterFactory";
import { IqmsRouterFactory } from "app/components/factories/IqmsRouterFactory";
import { ProjectRouterFactory } from "app/components/factories/ProjectRouterFactory";
import { KnowledgeRouterFactory } from "app/components/factories/KnowledgeRouterFactory";
import { isServiceEnabled } from "config/featureFlags";
import { WhatsNewModal } from 'app/components/whats-new/WhatsNewModal';
import { APP_VERSION } from 'AppConstants';
import { PageViewTracker } from 'analytics/PageViewTracker';

function App() {
  const [controller] = useMaterialUIController();
  const { direction, sidenavColor, darkMode, baseColor } = controller;
  const theme = useMemo(
    () => createAppTheme({ mode: darkMode ? "dark" : "light", baseColor }),
    [darkMode, baseColor]
  );

  const { pathname } = useLocation();

  // Removed mouse enter/leave handlers that were causing sidenav to collapse
  // The sidenav now stays in expanded state by default

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const renderRoutes = () => {
    const userRoles = getLoggedInUserRoles();
    // routes imported from routes.js
    if (isEmpty(userRoles)) return [];

    return routes.filter((route) =>
      route.allowedRoles.some((role) => userRoles.some((r) => r === role))
    );
  };

  var isValidUser = isAuthenticated();
  var currentToken = readSessionToken();

  if (!isValidUser || !currentToken) {
    return (
      <div className="App">
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ToastHost />
          <MDBox id="app-login-container">
            {/* Unauthenticated users can only reach login + the public forgot-password flow.
                The catch-all keeps the existing "any path shows login" behaviour. */}
            <Routes>
              <Route path="/forgot-password" element={<ForgotPassword />} key="forgot-password" />
              <Route path="*" element={<Login />} key="login-catchall" />
            </Routes>
          </MDBox>
        </ThemeProvider>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <div className="App">
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <WhatsNewModal appVersion={APP_VERSION} />
          <PageViewTracker />
          <ToastHost />

          <Sidenav
            color={sidenavColor}
            className="left-sidenav"
            brandName="InOpTra Digital"
            routes={renderRoutes()}
          />
          <MDBox id="app-main-container">
            <Header isLogin={isValidUser} />
            <Grid container direction="column" mt={2} sx={{ minHeight: "78vh" }}>
                <MDBox id="app-router-container">
                  <Routes>
                    <Route path="/dashboard" element={<DashboardRouterFactory />} key="dashboard" />
                    <Route path="/" element={<Navigate to={getLandingRoute()} replace />} key="root_redirect" />
                    <Route path="/signup" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} key="signup" />
                    <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} key="login" />
                    {/* <Route
                      path="/activities"
                      element={<TeamActivitiesRouterFactory />}
                      key="activities"
                    /> */}
                    <Route
                      path="/leave-plan"
                      element={<LeavePlanRouterFactory />}
                      key="leavey_plan"
                    />
                    <Route
                      path="/masters"
                      element={<MasterRouterFactory />}
                      key="masters"
                    />
                    <Route
                      path="/activity-types"
                      element={<ActivityTypeRouterFactory />}
                      key="activity-types"
                    />
                    <Route
                      path="/working-hours"
                      element={<WorkingHoursRouterFactory />}
                      key="working-hours"
                    />
                    <Route
                      path="/export-reports"
                      element={<ExportReportsRouterFactory />}
                      key="export-reports"
                    />
                    <Route
                      path="/change-password"
                      element={<ChangePasswordRouterFactory />}
                      key="change_password"
                    />
                    <Route
                      path="/admin-reset-password"
                      element={<AdminResetPasswordRouterFactory />}
                      key="admin_reset_password"
                    />
                    <Route path="/usage-analytics" element={<UsageAnalyticsRouterFactory />} key="usage-analytics" />
                    <Route path="/members" element={<TeamMembersRouterFactory />} key="members" />
                    {isServiceEnabled("skill-matrix") && (
                      <Route path="/skill-matrix" element={<SkillMatrixRouterFactory />} key="skill-matrix" />
                    )}
                    {isServiceEnabled("iqms") && (
                      <Route path="/iqms" element={<IqmsRouterFactory />} key="iqms" />
                    )}
                    {isServiceEnabled("project") && (
                      <Route path="/projects" element={<ProjectRouterFactory />} key="projects" />
                    )}
                    {isServiceEnabled("knowledge") && (
                      <Route path="/knowledge" element={<KnowledgeRouterFactory />} key="knowledge" />
                    )}
                    <Route path="/logout" element={<Logout />} key="logout" />
                  </Routes>
                </MDBox>
            </Grid>
            <Footer />
          </MDBox>
        </ThemeProvider>
      </div>
    </Provider>
  );
}

export default App;
